import React, {useEffect, useMemo, useState} from 'react';
import {createApp} from "@shopify/app-bridge";
import {API_KEY} from "../../App";
import {Redirect} from "@shopify/app-bridge/actions";
import {createBillingAPI, fetchRecommendationStatsAPI} from "../../api/axiosRequests";

interface RecommendationStatsProps {
    shop: string | null;
    showToast: (msg: string) => void;
    isBillingActive?: boolean;
}

interface StatsData {
    total_impressions: number;
    total_clicks: number;
    total_conversions?: number; // might be missing for free
}

export const LockIcon = () => (
    <svg width={20} height={20} strokeWidth={2} stroke="#ef4444" fill="none" viewBox="0 0 24 24">
        <rect x="5" y="11" width="14" height="8" rx="2" stroke="#ef4444"/>
        <path d="M7 11V7a5 5 0 0110 0v4" stroke="#ef4444"/>
    </svg>
);

export const Icon = ({type}: { type: 'eye' | 'cursor' | 'check' | 'chart' }) => {
    const style = {width: 20, height: 20, strokeWidth: 2, stroke: 'currentColor', fill: 'none'};
    switch (type) {
        case 'eye':
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                </svg>
            );
        case 'cursor':
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M3 2l6 20 2-8 8-2-16-10z"/>
                </svg>
            );
        case 'check':
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7"/>
                </svg>
            );
        case 'chart':
            return (
                <svg {...style} viewBox="0 0 24 24">
                    <path d="M4 19h16M4 15h8M4 11h4M4 7h12"/>
                </svg>
            );
        default:
            return null;
    }
};

const StatCard = ({
                      label,
                      value,
                      color,
                      icon,
                      tooltip,
                      locked = false,
                      onUpgrade,
                  }: {
    label: string;
    value: string | number;
    color: string;
    icon: React.ReactNode;
    tooltip: string;
    locked?: boolean;
    onUpgrade?: () => void;
}) => (
    <div
        role="group"
        aria-label={label}
        title={tooltip}
        tabIndex={0}
        style={{
            flex: '1 1 200px',
            background: '#ffffff',
            borderRadius: 14,
            border: `1px solid ${color}33`,
            padding: 20,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'default',
            transition: 'box-shadow 0.3s ease',
            opacity: locked ? 0.7 : 1,
            position: "relative",
        }}
        onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 4px 16px ${color}88`)}
        onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)')}
    >
        <div
            style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: `${color}22`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
                boxShadow: `inset 0 0 6px ${color}44`,
                color,
                fontSize: 24,
                lineHeight: '24px',
                position: 'relative',
            }}
        >
            {locked ? <LockIcon/> : icon}
        </div>
        <div style={{fontSize: 14, color: '#6b7280', fontWeight: 600, marginBottom: 10}}>{label}</div>
        <div style={{fontSize: 28, fontWeight: 700, color: '#111827', wordBreak: 'break-word'}}>
            {locked ?
                <button
                    style={{
                        background: "#ef4444",
                        color: "white",
                        border: "none",
                        padding: "6px 20px",
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 14,
                        marginTop: 8,
                        cursor: "pointer",
                        boxShadow: "0 2px 8px #ef444433"
                    }}
                    onClick={onUpgrade}
                >
                    Upgrade to unlock
                </button>
                : value}
        </div>
    </div>
);

export default function RecommendationStats({shop, showToast, isBillingActive}: RecommendationStatsProps) {
    const [stats, setStats] = useState<StatsData | null>(null);
    const [loading, setLoading] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const host = searchParams.get("host") || "";
    const appBridge = useMemo(() => createApp({apiKey: API_KEY, host, forceRedirect: true}), [host]);

    useEffect(() => {
        if (!shop) {
            setStats(null);
            return;
        }
        setLoading(true);
        fetchRecommendationStatsAPI(shop)
            .then(res => setStats(res.data))
            .catch(() => showToast('Failed to load stats'))
            .finally(() => setLoading(false));
    }, [shop, showToast]);

    const handleUpgrade = async () => {
        const billingRes = await createBillingAPI(shop, host);
        Redirect.create(appBridge).dispatch(Redirect.Action.REMOTE, billingRes.data.confirmationUrl);
    };

    if (loading && !stats) {
        return (
            <div style={{display: 'flex', gap: 16, marginBottom: 20}}>
                {Array.from({length: 4}).map((_, i) => (
                    <div
                        key={i}
                        style={{
                            flex: '1 1 200px',
                            height: 140,
                            borderRadius: 14,
                            background:
                                'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
                            animation: 'shimmer 1.8s infinite',
                        }}
                    />
                ))}
                <style>{`
          @keyframes shimmer {
            0% {
              background-position: -200px 0;
            }
            100% {
              background-position: 200px 0;
            }
          }
        `}</style>
            </div>
        );
    }

    if (!stats) return null;

    const metrics: any = [
        {
            label: 'Total Impressions',
            value: stats.total_impressions.toLocaleString(),
            color: '#3b82f6',
            icon: <Icon type="eye"/>,
            locked: false,
            tooltip: 'Number of times upsell recommendations were shown.'
        },
        {
            label: 'Total Clicks',
            value: stats.total_clicks.toLocaleString(),
            color: '#8b5cf6',
            icon: <Icon type="cursor"/>,
            locked: false,
            tooltip: 'Number of times upsell products were clicked.'
        }
    ];

    if (isBillingActive && typeof stats.total_conversions === "number") {
        const ctr = stats.total_clicks > 0 ?
            ((stats.total_conversions / stats.total_clicks) * 100).toFixed(2) + '%' :
            '0.00%';
        metrics.push(
            {
                label: 'Total Conversions',
                value: stats.total_conversions.toLocaleString(),
                color: '#10b981',
                icon: <Icon type="check"/>,
                locked: false,
                tooltip: 'Number of orders from upsell recommendations.'
            },
            {
                label: 'Click-to-Conversion Rate',
                value: ctr,
                color: '#ef4444',
                icon: <Icon type="chart"/>,
                locked: false,
                tooltip: 'The percentage of clicks that led to orders.'
            }
        );
    } else {
        metrics.push(
            {
                label: 'Total Conversions',
                value: '',
                color: '#10b981',
                icon: <Icon type="check"/>,
                tooltip: 'Upgrade required to view conversions.',
                locked: true,
                onUpgrade: handleUpgrade,
            },
            {
                label: 'Click-to-Conversion Rate',
                value: '',
                color: '#ef4444',
                icon: <Icon type="chart"/>,
                tooltip: 'Upgrade required to view conversion rate.',
                locked: true,
                onUpgrade: handleUpgrade,
            }
        );
    }

    return (
        <div style={{display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'space-evenly'}}>
            {metrics.map((m: any) => (
                <StatCard key={m.label} {...m} />
            ))}
        </div>
    );
}
